import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { Section } from "@/components/Section";
import { ContactForm } from "@/components/ContactForm";
import { SEO } from "@/components/SEO";
import { site } from "@/config/site";

export default function Contact() {
    const { search } = useLocation();
    const [defaultPackage, setDefaultPackage] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(search);
        setDefaultPackage(params.get("package") || "");
        setSuccess(params.get("success") === "1");
    }, [search]);

    return (
        <>
            <SEO title="Kontakt – Szymkas Agency" description="Skontaktuj się z Szymkas Agency. Telefon, e-mail i formularz." />

            <section className="bg-[#06111F] text-white pt-32 pb-16 md:pt-40 md:pb-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-faint opacity-50" />
                <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
                    <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">Kontakt</div>
                    <h1 className="font-display font-extrabold text-[38px] md:text-[58px] lg:text-[64px] leading-[1.05] tracking-tight">
                        Porozmawiajmy o<br /><span className="text-[#14B8A6]">Twoim projekcie</span>
                    </h1>
                    <p className="mt-6 text-[#AAB6C5] text-base md:text-lg max-w-2xl mx-auto">
                        Opisz krótko swoją firmę, potrzeby i cel. Skontaktujemy się z Tobą, aby ustalić najlepszy zakres współpracy.
                    </p>
                </div>
            </section>

            <Section variant="light">
                {success && (
                    <div className="max-w-3xl mx-auto mb-10 rounded-[16px] border border-[#14B8A6] bg-[#14B8A6]/10 p-5 text-[#0EA5A5]" data-testid="contact-success">
                        <strong>Dziękujemy za wiadomość.</strong> Skontaktujemy się z Tobą, aby poznać szczegóły projektu i przygotować ostateczną wycenę.
                    </div>
                )}
                <div className="grid lg:grid-cols-5 gap-10 items-start">
                    <div className="lg:col-span-2 space-y-6">
                        <div>
                            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0B1220]">Bezpośredni kontakt</h2>
                            <p className="mt-3 text-[#0B1220]/70 leading-relaxed">Wolisz porozmawiać bezpośrednio? Zadzwoń lub napisz – odpowiadamy zwykle tego samego dnia roboczego.</p>
                        </div>
                        <a href={`tel:${site.phoneLink}`} className="flex items-center gap-4 rounded-[16px] border border-[#E5E7EB] p-5 hover:border-[#14B8A6] transition-colors" data-testid="contact-phone-card">
                            <span className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 text-[#14B8A6] flex items-center justify-center"><Phone className="w-5 h-5" /></span>
                            <div>
                                <div className="text-xs text-[#0B1220]/60 uppercase tracking-wider">Telefon</div>
                                <div className="font-display font-semibold text-lg text-[#0B1220]">{site.phoneDisplay}</div>
                            </div>
                        </a>
                        <a href={`mailto:${site.email}`} className="flex items-center gap-4 rounded-[16px] border border-[#E5E7EB] p-5 hover:border-[#14B8A6] transition-colors" data-testid="contact-email-card">
                            <span className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 text-[#14B8A6] flex items-center justify-center"><Mail className="w-5 h-5" /></span>
                            <div>
                                <div className="text-xs text-[#0B1220]/60 uppercase tracking-wider">E-mail</div>
                                <div className="font-display font-semibold text-base md:text-lg text-[#0B1220] break-all">{site.email}</div>
                            </div>
                        </a>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="rounded-[20px] bg-white border border-[#E5E7EB] p-6 md:p-8 shadow-sm">
                            <ContactForm defaultPackage={defaultPackage} testidPrefix="contact-page" />
                        </div>
                    </div>
                </div>
            </Section>
        </>
    );
}
